/**
 * @file SenderSection.tsx
 * @description Блок «Отправление» настроек перевозчика (phase19.12).
 *
 * Первым делом отвечает на вопрос «точка отправления настроена или нет»:
 * без неё расчёт и передача отгрузок в СДЭК не работают, а по голым полям с
 * placeholder'ами это было незаметно.
 */

import { CheckCircle2, TriangleAlert } from "lucide-react";

import { SenderAddressSearch } from "./SenderAddressSearch";
import { SenderPointSection } from "./SenderPointSection";
import { useSenderAddressResolver } from "./useSenderAddressResolver";
import { Alert } from "../ui/alert";
import { Badge } from "../ui/badge";
import type { CarrierKind } from "../../lib/api-shop-carriers";
import { isSenderConfigured, type SenderFormValue } from "../../lib/carrier-sender";

interface Props {
  carrier: CarrierKind;
  value: SenderFormValue;
  onChange: (patch: Partial<SenderFormValue>) => void;
  /** Ключ Яндекс.Карт из настроек — нужен карте выбора пункта сдачи. */
  mapApiKey?: string | null;
  disabled?: boolean;
}

const MODES = [
  { key: "DROP_AT_PICKUP_POINT", label: "Сдаём сами в пункт выдачи" },
  { key: "COURIER_INTAKE", label: "Курьер забирает у нас" },
] as const;

export function SenderSection({
  carrier,
  value,
  onChange,
  mapApiKey,
  disabled,
}: Props) {
  const configured = isSenderConfigured(value);
  // Резолвер живёт здесь: распознанный город нужен и поиску, и выбору пункта.
  const resolver = useSenderAddressResolver({
    carrier,
    onChange,
    initialAddress: value.senderAddress,
  });

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
          Отправление
        </div>
        {configured ? (
          <Badge variant="success">
            <CheckCircle2 className="size-3" />
            Точка отправления настроена
          </Badge>
        ) : null}
      </div>

      {configured ? null : (
        <Alert severity="warning" icon={<TriangleAlert />}>
          Точка отправления не настроена — расчёт и передача отгрузок в СДЭК работать не
          будут.
        </Alert>
      )}

      <fieldset className="flex flex-col gap-2" disabled={disabled}>
        <legend className="mb-1 text-sm">Способ передачи</legend>
        {MODES.map((mode) => (
          <label key={mode.key} className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="radio"
              name="handoverMode"
              checked={value.handoverMode === mode.key}
              onChange={() => onChange({ handoverMode: mode.key })}
            />
            {mode.label}
          </label>
        ))}
      </fieldset>

      {value.handoverMode === "DROP_AT_PICKUP_POINT" ? (
        <SenderPointSection
          carrier={carrier}
          code={value.senderPickupPointCode}
          city={resolver.cityName ?? undefined}
          mapApiKey={mapApiKey}
          disabled={disabled}
          onChange={(code) => onChange({ senderPickupPointCode: code })}
        />
      ) : null}

      <SenderAddressSearch
        value={value}
        onChange={onChange}
        resolver={resolver}
        disabled={disabled}
      />
    </section>
  );
}
